<template>
  <div class="container mx-auto sm:mt-16 mt-8">
    <div
      class="grid pt-16 px-4 sm:px-8 xl:px-12 lg:pt-20 bg-opacity-50 pb-12 lg:pb-22 rounded-4xl justify-items-center bg-lavender-white"
    >
      <div class="mx-auto mb-4">
        <AirIcon class="w-16 h-16" />
      </div>
      <h5 class="text-black text-2xl lg:text-3xl">
        {{ interdata.data.subtitle }}
      </h5>
      <h2
        class="max-w-6xl px-4 mx-auto mt-4 text-center text-2xl sm:text-3xl md:text-4xl lg:text-5xl"
      >
        {{ interdata.data.title }}
      </h2>
      <div class="flex md:flex-row flex-col gap-10 mt-8 md:mt-14">
        <div class="w-full md:w-1/3 h-full flex items-center justify-center">
          <nuxt-img
            loading="lazy"
            format="webp"
            src="/central-park.jpg"
            alt="central-park"
            class="max-w-full w-96 rounded-lg drop-shadow-md"
          ></nuxt-img>
        </div>
        <div class="w-full md:w-2/3 h-full">
          <div
            class="sm:py-8 py-0 lg:text-lg"
            v-html="interdata.data.content"
          ></div>
        </div>
      </div>
      <div class="mt-6 md:mt-20">
        <h4
          class="text-xl sm:text-2xl lg:text-3xl font-semibold mb-8 sm:mb-12 text-center"
        >
          {{ realdata.any_content.title }}
        </h4>
        <div class="grid grid-cols-12 gap-6 sm:gap-10">
          <div
            class="col-span-12 md:col-span-6 h-full"
            v-for="example in realdata.data"
          >
            <div class="flex flex-col gap-6 h-full">
              <div
                class="flex flex-col p-5 rounded-2xl bg-white drop-shadow-md gap-4 h-full"
              >
                <nuxt-img
                  loading="lazy"
                  alt="brand-using-qr-code"
                  :src="example.icon"
                  format="webp"
                  :width="64"
                ></nuxt-img>
                <div v-html="example.content"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import AirIcon from "~/assets/svg/marketing/air-earth.svg?component";
import DataLocale from "~~/config/interface/DataLocale";
import MultiDataLocale from "~~/config/interface/MultiDataLocale";

export default defineComponent({
  components: {
    AirIcon,
  },
  setup() {
    const interdata = new DataLocale([
      {
        locale: "en",
        title: "Harnessing QR Codes for Engagement",
        subtitle: "Interactive Marketing",
        content:
          "<p class='mb-2'> In 2022, the <b class='text-success'>Central Park Conservancy</b> in <b>New York City</b> introduced a QR code campaign aimed at enhancing the experience for both residents and tourists by making it more interactive and engaging. </p> <p class='mb-2'> Instructional signs were placed throughout Central Park, each featuring a unique QR code. Scanning the code directed visitors to an interactive website where they could access detailed information, images, and other engaging content such as maps, videos, and tour guides. </p> <p class='mb-2'> The campaign’s success stemmed from its ability to offer a fresh and immersive experience, allowing visitors to learn about Central Park’s history in a more interactive, enjoyable manner. </p> <p> Moreover, the use of QR codes allowed the Central Park Conservancy to gather valuable data on the most visited areas, providing insights that informed the creation of tailored entertainment programs and services designed to meet the needs of their audience. </p>",
      },
      {
        locale: "fr",
        title: "Utiliser les codes QR pour l'engagement",
        subtitle: "Marketing interactif",
        content:
          "<p class='mb-2'> En 2022, le <b class='text-success'>Central Park Conservancy</b> à <b>New York City</b> a lancé une campagne de codes QR visant à améliorer l'expérience des résidents et des touristes en la rendant plus interactive et engageante. </p> <p class='mb-2'> Des panneaux d'information ont été placés dans tout Central Park, chacun comportant un code QR unique. En scannant le code, les visiteurs étaient dirigés vers un site web interactif où ils pouvaient accéder à des informations détaillées, des images et d'autres contenus attrayants tels que des cartes, des vidéos et des guides touristiques. </p> <p class='mb-2'> Le succès de la campagne est dû à sa capacité à offrir une expérience nouvelle et immersive, permettant aux visiteurs de découvrir l'histoire de Central Park de manière plus interactive et agréable. </p> <p> De plus, l'utilisation des codes QR a permis au Central Park Conservancy de recueillir des données précieuses sur les zones les plus visitées, fournissant des informations qui ont éclairé la création de programmes de divertissement et de services adaptés aux besoins de leur public. </p>",
      },
      {
        locale: "it",
        title: "Sfruttare i codici QR per l'engagement",
        subtitle: "Marketing interattivo",
        content:
          "<p class='mb-2'> Nel 2022, il <b class='text-success'>Central Park Conservancy</b> a <b>New York City</b> ha introdotto una campagna di codici QR volta a migliorare l'esperienza sia per i residenti che per i turisti, rendendola più interattiva e coinvolgente. </p> <p class='mb-2'> Segnali informativi sono stati posizionati in tutto Central Park, ognuno con un codice QR univoco. La scansione del codice indirizzava i visitatori a un sito web interattivo dove potevano accedere a informazioni dettagliate, immagini e altri contenuti coinvolgenti come mappe, video e guide turistiche. </p> <p class='mb-2'> Il successo della campagna è derivato dalla sua capacità di offrire un'esperienza fresca e coinvolgente, consentendo ai visitatori di conoscere la storia di Central Park in modo più interattivo e piacevole. </p> <p> Inoltre, l'uso di codici QR ha consentito al Central Park Conservancy di raccogliere dati preziosi sulle aree più visitate, fornendo informazioni che hanno guidato la creazione di programmi di intrattenimento e servizi su misura progettati per soddisfare le esigenze del loro pubblico. </p>",
      },
      {
        locale: "de",
        title: "QR-Codes zur Interaktion nutzen",
        subtitle: "Interaktives Marketing",
        content:
          "<p class='mb-2'> Im Jahr 2022 führte die <b class='text-success'>Central Park Conservancy</b> in <b>New York City</b> eine QR-Code-Kampagne ein, die darauf abzielte, das Erlebnis für Einwohner und Touristen durch mehr Interaktivität und Engagement zu verbessern. </p> <p class='mb-2'> In ganz Central Park wurden Hinweisschilder aufgestellt, die jeweils mit einem einzigartigen QR-Code versehen waren. Durch das Scannen des Codes wurden die Besucher auf eine interaktive Website geleitet, auf der sie auf detaillierte Informationen, Bilder und andere ansprechende Inhalte wie Karten, Videos und Reiseführer zugreifen konnten. </p> <p class='mb-2'> Der Erfolg der Kampagne beruhte auf ihrer Fähigkeit, ein frisches und immersives Erlebnis zu bieten, das es den Besuchern ermöglicht, die Geschichte des Central Parks auf interaktivere und angenehmere Weise kennenzulernen. </p> <p> Darüber hinaus ermöglichte die Verwendung von QR-Codes der Central Park Conservancy, wertvolle Daten über die am meisten besuchten Bereiche zu sammeln, die Erkenntnisse lieferten, die die Erstellung von maßgeschneiderten Unterhaltungsprogrammen und Dienstleistungen zur Deckung der Bedürfnisse ihres Publikums beeinflussten. </p>",
      },
      {
        locale: "es",
        title: "Aprovechando los códigos QR para la participación",
        subtitle: "Marketing interactivo",
        content:
          "<p class='mb-2'> En 2022, la <b class='text-success'>Central Park Conservancy</b> en <b>Nueva York</b> introdujo una campaña de códigos QR destinada a mejorar la experiencia tanto para los residentes como para los turistas haciéndola más interactiva y atractiva. </p> <p class='mb-2'> Se colocaron señales instructivas en todo Central Park, cada una con un código QR único. Al escanear el código, los visitantes eran dirigidos a un sitio web interactivo donde podían acceder a información detallada, imágenes y otros contenidos atractivos como mapas, videos y guías turísticos. </p> <p class='mb-2'> El éxito de la campaña se debió a su capacidad para ofrecer una experiencia fresca e inmersiva, permitiendo a los visitantes aprender sobre la historia de Central Park de una manera más interactiva y agradable. </p> <p> Además, el uso de códigos QR permitió a la Central Park Conservancy recopilar datos valiosos sobre las áreas más visitadas, proporcionando información que sirvió de base para la creación de programas de entretenimiento y servicios personalizados diseñados para satisfacer las necesidades de su público. </p>",
      },
      {
        locale: "nl",
        title: "QR-codes gebruiken voor betrokkenheid",
        subtitle: "Interactieve marketing",
        content:
          "<p class='mb-2'> In 2022 introduceerde de <b class='text-success'>Central Park Conservancy</b> in <b>New York City</b> een QR-codecampagne gericht op het verbeteren van de ervaring voor zowel bewoners als toeristen door deze interactiever en boeiender te maken. </p> <p class='mb-2'> Door heel Central Park werden instructieborden geplaatst, elk met een unieke QR-code. Het scannen van de code leidde bezoekers naar een interactieve website waar ze toegang hadden tot gedetailleerde informatie, afbeeldingen en andere boeiende content zoals kaarten, video's en tourgidsen. </p> <p class='mb-2'> Het succes van de campagne kwam voort uit het vermogen om een frisse en meeslepende ervaring te bieden, waardoor bezoekers op een meer interactieve en plezierige manier meer te weten kwamen over de geschiedenis van Central Park. </p> <p> Bovendien stelde het gebruik van QR-codes de Central Park Conservancy in staat om waardevolle gegevens te verzamelen over de meest bezochte gebieden, wat inzichten opleverde die de basis vormden voor de creatie van op maat gemaakte entertainmentprogramma's en -diensten die zijn ontworpen om aan de behoeften van hun publiek te voldoen. </p>",
      },
      {
        locale: "pt",
        title: "Aproveitando Códigos QR para Engajamento",
        subtitle: "Marketing Interativo",
        content:
          "<p class='mb-2'> Em 2022, o <b class='text-success'>Central Park Conservancy</b> em <b>Nova York</b> introduziu uma campanha de código QR com o objetivo de melhorar a experiência para residentes e turistas, tornando-a mais interativa e envolvente. </p> <p class='mb-2'> Sinais instrucionais foram colocados em todo o Central Park, cada um com um código QR exclusivo. A digitalização do código direcionava os visitantes para um site interativo onde podiam acessar informações detalhadas, imagens e outros conteúdos envolventes, como mapas, vídeos e guias turísticos. </p> <p class='mb-2'> O sucesso da campanha decorreu da sua capacidade de oferecer uma experiência nova e imersiva, permitindo aos visitantes aprender sobre a história do Central Park de uma forma mais interativa e agradável. </p> <p> Além disso, o uso de códigos QR permitiu ao Central Park Conservancy coletar dados valiosos sobre as áreas mais visitadas, fornecendo informações que informaram a criação de programas de entretenimento e serviços personalizados, projetados para atender às necessidades de seu público. </p>",
      },
      {
        locale: "ja",
        title: "エンゲージメントのためのQRコードの活用",
        subtitle: "インタラクティブマーケティング",
        content:
          "<p class='mb-2'> 2022年、<b>ニューヨーク市</b>の<b class='text-success'>セントラルパークコンサーバンシー</b>は、居住者と観光客の両方の体験をよりインタラクティブで魅力的なものにすることを目指したQRコードキャンペーンを導入しました。 </p> <p class='mb-2'> セントラルパーク全体に説明標識が設置され、それぞれに独自のQRコードが付いていました。コードをスキャンすると、訪問者は地図、ビデオ、ツアーガイドなどの詳細な情報、画像、その他の魅力的なコンテンツにアクセスできるインタラクティブなウェブサイトに移動しました。 </p> <p class='mb-2'> このキャンペーンの成功は、新鮮で没入感のある体験を提供し、訪問者がよりインタラクティブで楽しい方法でセントラルパークの歴史を学ぶことができるようにしたことに起因しています。 </p> <p> さらに、QRコードの使用により、セントラルパークコンサーバンシーは最も訪問されたエリアに関する貴重なデータを収集することができ、視聴者のニーズを満たすように設計された、カスタマイズされたエンターテイメントプログラムとサービスの作成に役立つ洞察を提供しました。 </p>",
      },
      {
        locale: "zh",
        title: "利用二维码促进互动",
        subtitle: "互动营销",
        content:
          "<p class='mb-2'> 2022年，<b>纽约市</b>的<b class='text-success'>中央公园保护协会</b>推出了一项二维码活动，旨在通过使其更具互动性和吸引力，来提升居民和游客的体验。 </p> <p class='mb-2'> 在中央公园各处都放置了带有独特二维码的说明标志。扫描二维码将游客引导至一个互动网站，在那里他们可以访问详细信息、图像以及其他引人入胜的内容，例如地图、视频和导游。 </p> <p class='mb-2'> 该活动的成功源于其提供全新沉浸式体验的能力，使游客能够以更具互动性和趣味性的方式了解中央公园的历史。 </p> <p> 此外，使用二维码使中央公园保护协会能够收集有关访问量最大的区域的宝贵数据，从而为创建量身定制的娱乐节目和服务提供见解，旨在满足其受众的需求。 </p>",
      },
      {
        locale: "vi",
        title: "Khai thác Mã QR để Tương tác",
        subtitle: "Marketing Tương tác",
        content:
          "<p class='mb-2'> Năm 2022, <b class='text-success'>Tổ chức Bảo tồn Công viên Trung tâm</b> ở <b>Thành phố New York</b> đã giới thiệu một chiến dịch mã QR nhằm nâng cao trải nghiệm cho cả cư dân và khách du lịch bằng cách làm cho nó trở nên tương tác và hấp dẫn hơn. </p> <p class='mb-2'> Các biển hướng dẫn được đặt khắp Công viên Trung tâm, mỗi biển có một mã QR riêng. Quét mã sẽ hướng khách tham quan đến một trang web tương tác, nơi họ có thể truy cập thông tin chi tiết, hình ảnh và nội dung hấp dẫn khác như bản đồ, video và hướng dẫn viên du lịch. </p> <p class='mb-2'> Thành công của chiến dịch bắt nguồn từ khả năng mang đến trải nghiệm mới mẻ và hấp dẫn, cho phép du khách tìm hiểu về lịch sử của Công viên Trung tâm một cách tương tác và thú vị hơn. </p> <p> Hơn nữa, việc sử dụng mã QR cho phép Tổ chức Bảo tồn Công viên Trung tâm thu thập dữ liệu có giá trị về các khu vực được ghé thăm nhiều nhất, cung cấp thông tin chi tiết giúp hình thành việc tạo ra các chương trình và dịch vụ giải trí phù hợp được thiết kế để đáp ứng nhu cầu của khán giả. </p>",
      },
      {
        locale: "id",
        title: "Memanfaatkan Kode QR untuk Keterlibatan",
        subtitle: "Pemasaran Interaktif",
        content:
          "<p class='mb-2'> Pada tahun 2022, <b class='text-success'>Central Park Conservancy</b> di <b>New York City</b> memperkenalkan kampanye kode QR yang bertujuan untuk meningkatkan pengalaman bagi penduduk dan turis dengan membuatnya lebih interaktif dan menarik. </p> <p class='mb-2'> Tanda-tanda instruksi ditempatkan di seluruh Central Park, masing-masing menampilkan kode QR yang unik. Pemindaian kode mengarahkan pengunjung ke situs web interaktif tempat mereka dapat mengakses informasi rinci, gambar, dan konten menarik lainnya seperti peta, video, dan pemandu wisata. </p> <p class='mb-2'> Keberhasilan kampanye ini berasal dari kemampuannya untuk menawarkan pengalaman yang segar dan mendalam, memungkinkan pengunjung untuk belajar tentang sejarah Central Park dengan cara yang lebih interaktif dan menyenangkan. </p> <p> Selain itu, penggunaan kode QR memungkinkan Central Park Conservancy untuk mengumpulkan data berharga tentang area yang paling banyak dikunjungi, memberikan wawasan yang menginformasikan pembuatan program dan layanan hiburan yang disesuaikan yang dirancang untuk memenuhi kebutuhan audiens mereka. </p>",
      },
      {
        locale: "tr",
        title: "Etkileşim İçin QR Kodlarından Yararlanma",
        subtitle: "İnteraktif Pazarlama",
        content:
          "<p class='mb-2'> 2022'de <b>New York City'deki</b> <b class='text-success'>Central Park Conservancy</b>, hem sakinler hem de turistler için deneyimi daha interaktif ve ilgi çekici hale getirerek geliştirmeyi amaçlayan bir QR kod kampanyası başlattı. </p> <p class='mb-2'> Central Park'ın her yerine, her biri benzersiz bir QR kodu içeren bilgilendirici tabelalar yerleştirildi. Kodu taramak, ziyaretçileri haritalar, videolar ve tur rehberleri gibi ayrıntılı bilgilere, görüntülere ve diğer ilgi çekici içeriklere erişebilecekleri interaktif bir web sitesine yönlendirdi. </p> <p class='mb-2'> Kampanyanın başarısı, ziyaretçilerin Central Park'ın tarihi hakkında daha etkileşimli, keyifli bir şekilde bilgi edinmelerini sağlayarak yeni ve sürükleyici bir deneyim sunma becerisinden kaynaklanıyordu. </p> <p> Ayrıca, QR kodlarının kullanımı, Central Park Conservancy'nin en çok ziyaret edilen alanlar hakkında değerli veriler toplamasına olanak tanıyarak, hedef kitlelerinin ihtiyaçlarını karşılamak üzere tasarlanmış özel eğlence programları ve hizmetlerinin oluşturulmasına katkıda bulunan içgörüler sağladı. </p>",
      },
      {
        locale: "hi",
        title: "जुड़ाव के लिए क्यूआर कोड का उपयोग",
        subtitle: "इंटरैक्टिव मार्केटिंग",
        content:
          "<p class='mb-2'> 2022 में, <b>न्यूयॉर्क शहर</b> में <b class='text-success'>सेंट्रल पार्क कंज़र्वेंसी</b> ने एक क्यूआर कोड अभियान शुरू किया जिसका उद्देश्य निवासियों और पर्यटकों दोनों के लिए अनुभव को अधिक इंटरैक्टिव और आकर्षक बनाकर बढ़ाना था। </p> <p class='mb-2'> सेंट्रल पार्क में निर्देशनात्मक संकेत लगाए गए थे, जिनमें से प्रत्येक में एक अद्वितीय क्यूआर कोड था। कोड को स्कैन करने से आगंतुकों को एक इंटरैक्टिव वेबसाइट पर निर्देशित किया गया, जहां वे विस्तृत जानकारी, छवियों और मानचित्र, वीडियो और टूर गाइड जैसी अन्य आकर्षक सामग्री तक पहुंच सकते थे। </p> <p class='mb-2'> अभियान की सफलता इस तथ्य से उपजी है कि यह एक ताज़ा और गहन अनुभव प्रदान करने में सक्षम है, जिससे आगंतुक सेंट्रल पार्क के इतिहास को अधिक इंटरैक्टिव, सुखद तरीके से जान सकते हैं। </p> <p> इसके अलावा, क्यूआर कोड के उपयोग ने सेंट्रल पार्क कंज़र्वेंसी को सबसे अधिक देखे जाने वाले क्षेत्रों पर बहुमूल्य डेटा एकत्र करने की अनुमति दी, जिससे उन जानकारियों को प्राप्त किया गया जिन्होंने उनके दर्शकों की जरूरतों को पूरा करने के लिए डिज़ाइन किए गए अनुरूप मनोरंजन कार्यक्रमों और सेवाओं के निर्माण की जानकारी दी। </p>",
      },
      {
        locale: "th",
        title: "การใช้ประโยชน์จากคิวอาร์โค้ดเพื่อการมีส่วนร่วม",
        subtitle: "การตลาดเชิงโต้ตอบ",
        content:
          "<p class='mb-2'> ในปี 2022 <b class='text-success'>Central Park Conservancy</b> ใน <b>นิวยอร์กซิตี้</b> ได้เปิดตัวแคมเปญคิวอาร์โค้ด โดยมีเป้าหมายเพื่อยกระดับประสบการณ์สำหรับทั้งผู้อยู่อาศัยและนักท่องเที่ยว โดยทำให้มีความโต้ตอบและน่าสนใจมากยิ่งขึ้น </p> <p class='mb-2'> ป้ายแนะนำถูกติดตั้งทั่วทั้งเซ็นทรัลพาร์ค โดยแต่ละป้ายมีคิวอาร์โค้ดที่ไม่ซ้ำกัน การสแกนโค้ดจะนำผู้เยี่ยมชมไปยังเว็บไซต์เชิงโต้ตอบ ซึ่งพวกเขาสามารถเข้าถึงข้อมูลโดยละเอียด รูปภาพ และเนื้อหาที่น่าสนใจอื่นๆ เช่น แผนที่ วิดีโอ และมัคคุเทศก์ </p> <p class='mb-2'> ความสำเร็จของแคมเปญเกิดจากความสามารถในการนำเสนอประสบการณ์ที่แปลกใหม่และดื่มด่ำ ทำให้ผู้เยี่ยมชมสามารถเรียนรู้เกี่ยวกับประวัติของเซ็นทรัลพาร์คในลักษณะที่มีการโต้ตอบและสนุกสนานมากยิ่งขึ้น </p> <p> นอกจากนี้ การใช้คิวอาร์โค้ดยังช่วยให้ Central Park Conservancy สามารถรวบรวมข้อมูลที่มีค่าเกี่ยวกับพื้นที่ที่มีผู้เข้าชมมากที่สุด ซึ่งให้ข้อมูลเชิงลึกที่แจ้งให้ทราบถึงการสร้างโปรแกรมความบันเทิงและบริการที่ปรับให้เหมาะสม ซึ่งออกแบบมาเพื่อตอบสนองความต้องการของผู้ชม </p>",
      },
    ]);

    const realdata = new MultiDataLocale([
      {
        locale: "en",
        title: "Real-world Applications of QR Codes Across Industries",
        data: [
          {
            icon: "/icons/delta-airlines-96.png",
            content:
              "<b>Delta Airlines</b> enhances the flight check-in process by integrating QR codes on boarding passes. Passengers simply scan the code to access real-time flight details directly on their smartphones.",
          },
          {
            icon: "/icons/starbucks-96.png",
            content:
              "<b>Starbucks</b> leverages QR codes to simplify both the ordering system and customer loyalty rewards. By scanning codes at checkout or on products, customers effortlessly collect points and redeem rewards.",
          },
          {
            icon: "/icons/coca-cola-96.png",
            content:
              "In the food and beverage sector, <b>Coca-Cola</b> uses QR codes to involve customers in interactive games and sweepstakes. Customers scan codes found on bottle caps to participate and win prizes.",
          },
          {
            icon: "/icons/museum-64.png",
            content:
              "<b>Libraries</b> and <b>museums</b> integrate QR codes into their exhibits to create interactive learning experiences. Visitors scan the codes to access comprehensive information, videos, and guides about the exhibits.",
          },
        ],
      },
      {
        locale: "fr",
        title: "Applications concrètes des codes QR dans divers secteurs",
        data: [
          {
            icon: "/icons/delta-airlines-96.png",
            content:
              "<b>Delta Airlines</b> améliore le processus d'enregistrement des vols en intégrant des codes QR sur les cartes d'embarquement. Les passagers scannent simplement le code pour accéder aux détails du vol en temps réel directement sur leurs smartphones.",
          },
          {
            icon: "/icons/starbucks-96.png",
            content:
              "<b>Starbucks</b> utilise les codes QR pour simplifier à la fois le système de commande et les récompenses de fidélité client. En scannant les codes à la caisse ou sur les produits, les clients collectent facilement des points et échangent des récompenses.",
          },
          {
            icon: "/icons/coca-cola-96.png",
            content:
              "Dans le secteur de l'alimentation et des boissons, <b>Coca-Cola</b> utilise des codes QR pour impliquer les clients dans des jeux interactifs et des tirages au sort. Les clients scannent les codes trouvés sur les bouchons de bouteilles pour participer et gagner des prix.",
          },
          {
            icon: "/icons/museum-64.png",
            content:
              "Les <b>bibliothèques</b> et les <b>musées</b> intègrent des codes QR dans leurs expositions pour créer des expériences d'apprentissage interactives. Les visiteurs scannent les codes pour accéder à des informations complètes, des vidéos et des guides sur les expositions.",
          },
        ],
      },
      {
        locale: "it",
        title: "Applicazioni reali dei codici QR in vari settori",
        data: [
          {
            icon: "/icons/delta-airlines-96.png",
            content:
              "<b>Delta Airlines</b> migliora il processo di check-in dei voli integrando codici QR sulle carte d'imbarco. I passeggeri scansionano semplicemente il codice per accedere ai dettagli del volo in tempo reale direttamente sui loro smartphone.",
          },
          {
            icon: "/icons/starbucks-96.png",
            content:
              "<b>Starbucks</b> sfrutta i codici QR per semplificare sia il sistema di ordinazione che i premi fedeltà dei clienti. Scansionando i codici alla cassa o sui prodotti, i clienti raccolgono facilmente punti e riscattano premi.",
          },
          {
            icon: "/icons/coca-cola-96.png",
            content:
              "Nel settore alimentare e delle bevande, <b>Coca-Cola</b> utilizza i codici QR per coinvolgere i clienti in giochi interattivi e lotterie. I clienti scansionano i codici trovati sui tappi delle bottiglie per partecipare e vincere premi.",
          },
          {
            icon: "/icons/museum-64.png",
            content:
              "<b>Biblioteche</b> e <b>musei</b> integrano i codici QR nelle loro mostre per creare esperienze di apprendimento interattive. I visitatori scansionano i codici per accedere a informazioni complete, video e guide sulle mostre.",
          },
        ],
      },
      {
        locale: "de",
        title: "Reale Anwendungen von QR-Codes in verschiedenen Branchen",
        data: [
          {
            icon: "/icons/delta-airlines-96.png",
            content:
              "<b>Delta Airlines</b> verbessert den Check-in-Prozess für Flüge durch die Integration von QR-Codes auf Bordkarten. Passagiere scannen einfach den Code, um direkt auf ihren Smartphones auf Echtzeit-Flugdetails zuzugreifen.",
          },
          {
            icon: "/icons/starbucks-96.png",
            content:
              "<b>Starbucks</b> nutzt QR-Codes, um sowohl das Bestellsystem als auch die Kundenbindungsprogramme zu vereinfachen. Durch das Scannen von Codes an der Kasse oder auf Produkten sammeln Kunden mühelos Punkte und lösen Prämien ein.",
          },
          {
            icon: "/icons/coca-cola-96.png",
            content:
              "Im Lebensmittel- und Getränkesektor verwendet <b>Coca-Cola</b> QR-Codes, um Kunden in interaktive Spiele und Gewinnspiele einzubeziehen. Kunden scannen Codes auf Flaschenverschlüssen, um teilzunehmen und Preise zu gewinnen.",
          },
          {
            icon: "/icons/museum-64.png",
            content:
              "<b>Bibliotheken</b> und <b>Museen</b> integrieren QR-Codes in ihre Ausstellungen, um interaktive Lernerfahrungen zu schaffen. Besucher scannen die Codes, um auf umfassende Informationen, Videos und Leitfäden zu den Ausstellungen zuzugreifen.",
          },
        ],
      },
      {
        locale: "es",
        title: "Aplicaciones reales de los códigos QR en diversas industrias",
        data: [
          {
            icon: "/icons/delta-airlines-96.png",
            content:
              "<b>Delta Airlines</b> mejora el proceso de facturación de vuelos al integrar códigos QR en las tarjetas de embarque. Los pasajeros simplemente escanean el código para acceder a los detalles del vuelo en tiempo real directamente en sus teléfonos inteligentes.",
          },
          {
            icon: "/icons/starbucks-96.png",
            content:
              "<b>Starbucks</b> aprovecha los códigos QR para simplificar tanto el sistema de pedidos como las recompensas de fidelidad del cliente. Al escanear los códigos en la caja o en los productos, los clientes acumulan puntos y canjean recompensas sin esfuerzo.",
          },
          {
            icon: "/icons/coca-cola-96.png",
            content:
              "En el sector de alimentos y bebidas, <b>Coca-Cola</b> utiliza códigos QR para involucrar a los clientes en juegos interactivos y sorteos. Los clientes escanean los códigos que se encuentran en las tapas de las botellas para participar y ganar premios.",
          },
          {
            icon: "/icons/museum-64.png",
            content:
              "Las <b>bibliotecas</b> y los <b>museos</b> integran códigos QR en sus exposiciones para crear experiencias de aprendizaje interactivas. Los visitantes escanean los códigos para acceder a información completa, videos y guías sobre las exposiciones.",
          },
        ],
      },
      {
        locale: "nl",
        title: "Praktijktoepassingen van QR-codes in verschillende industrieën",
        data: [
          {
            icon: "/icons/delta-airlines-96.png",
            content:
              "<b>Delta Airlines</b> verbetert het incheckproces voor vluchten door QR-codes op instapkaarten te integreren. Passagiers scannen eenvoudig de code om direct op hun smartphones toegang te krijgen tot real-time vluchtgegevens.",
          },
          {
            icon: "/icons/starbucks-96.png",
            content:
              "<b>Starbucks</b> maakt gebruik van QR-codes om zowel het bestelsysteem als de klantenloyaliteitsbeloningen te vereenvoudigen. Door codes bij de kassa of op producten te scannen, verzamelen klanten moeiteloos punten en wisselen ze beloningen in.",
          },
          {
            icon: "/icons/coca-cola-96.png",
            content:
              "In de voedings- en drankensector gebruikt <b>Coca-Cola</b> QR-codes om klanten te betrekken bij interactieve spellen en loterijen. Klanten scannen codes die op de doppen van flessen zijn gevonden om deel te nemen en prijzen te winnen.",
          },
          {
            icon: "/icons/museum-64.png",
            content:
              "<b>Bibliotheken</b> en <b>musea</b> integreren QR-codes in hun tentoonstellingen om interactieve leerervaringen te creëren. Bezoekers scannen de codes om toegang te krijgen tot uitgebreide informatie, video's en gidsen over de tentoonstellingen.",
          },
        ],
      },
      {
        locale: "pt",
        title: "Aplicações reais de códigos QR em vários setores",
        data: [
          {
            icon: "/icons/delta-airlines-96.png",
            content:
              "A <b>Delta Airlines</b> aprimora o processo de check-in de voos integrando códigos QR em cartões de embarque. Os passageiros simplesmente escaneiam o código para acessar detalhes de voo em tempo real diretamente em seus smartphones.",
          },
          {
            icon: "/icons/starbucks-96.png",
            content:
              "A <b>Starbucks</b> utiliza códigos QR para simplificar tanto o sistema de pedidos quanto as recompensas de fidelidade do cliente. Ao escanear códigos no caixa ou em produtos, os clientes acumulam pontos sem esforço e resgatam recompensas.",
          },
          {
            icon: "/icons/coca-cola-96.png",
            content:
              "No setor de alimentos e bebidas, a <b>Coca-Cola</b> usa códigos QR para envolver os clientes em jogos interativos e sorteios. Os clientes escaneiam os códigos encontrados nas tampas de garrafas para participar e ganhar prêmios.",
          },
          {
            icon: "/icons/museum-64.png",
            content:
              "<b>Bibliotecas</b> e <b>museus</b> integram códigos QR em suas exposições para criar experiências de aprendizagem interativas. Os visitantes escaneiam os códigos para acessar informações abrangentes, vídeos e guias sobre as exposições.",
          },
        ],
      },
      {
        locale: "ja",
        title: "業界を超えたQRコードの現実的な応用",
        data: [
          {
            icon: "/icons/delta-airlines-96.png",
            content:
              "<b>デルタ航空</b>は、搭乗券にQRコードを統合することで、フライトチェックインプロセスを強化しています。乗客はコードをスキャンするだけで、スマートフォンでリアルタイムのフライト詳細に直接アクセスできます。",
          },
          {
            icon: "/icons/starbucks-96.png",
            content:
              "<b>スターバックス</b>は、注文システムと顧客ロイヤルティ特典の両方を簡素化するためにQRコードを活用しています。レジまたは製品のコードをスキャンすることで、顧客はポイントを簡単に収集し、特典と交換できます。",
          },
          {
            icon: "/icons/coca-cola-96.png",
            content:
              "食品および飲料セクターでは、<b>コカ・コーラ</b>はQRコードを使用して、インタラクティブゲームや懸賞に顧客を参加させています。顧客はボトルキャップにあるコードをスキャンして参加し、賞品を獲得します。",
          },
          {
            icon: "/icons/museum-64.png",
            content:
              "<b>図書館</b>や<b>博物館</b>は、インタラクティブな学習体験を生み出すために、展示にQRコードを統合しています。訪問者はコードをスキャンして、展示に関する包括的な情報、ビデオ、ガイドにアクセスします。",
          },
        ],
      },
      {
        locale: "zh",
        title: "二维码在各行业的实际应用",
        data: [
          {
            icon: "/icons/delta-airlines-96.png",
            content:
              "<b>达美航空</b>通过在登机牌上集成二维码来增强航班值机流程。乘客只需扫描代码即可直接在智能手机上访问实时航班详细信息。",
          },
          {
            icon: "/icons/starbucks-96.png",
            content:
              "<b>星巴克</b>利用二维码简化订购系统和客户忠诚度奖励。通过扫描结账或产品上的代码，客户可以轻松累积积分并兑换奖励。",
          },
          {
            icon: "/icons/coca-cola-96.png",
            content:
              "在食品和饮料行业，<b>可口可乐</b>使用二维码让客户参与互动游戏和抽奖活动。客户扫描瓶盖上的代码即可参与并赢取奖品。",
          },
          {
            icon: "/icons/museum-64.png",
            content:
              "<b>图书馆</b>和<b>博物馆</b>将二维码集成到展览中，以创造互动式学习体验。参观者扫描代码以访问有关展览的全面信息、视频和指南。",
          },
        ],
      },
      {
        locale: "vi",
        title: "Ứng dụng thực tế của mã QR trong các ngành công nghiệp",
        data: [
          {
            icon: "/icons/delta-airlines-96.png",
            content:
              "<b>Delta Airlines</b> nâng cao quy trình làm thủ tục chuyến bay bằng cách tích hợp mã QR trên thẻ lên máy bay. Hành khách chỉ cần quét mã để truy cập thông tin chi tiết về chuyến bay theo thời gian thực trực tiếp trên điện thoại thông minh của họ.",
          },
          {
            icon: "/icons/starbucks-96.png",
            content:
              "<b>Starbucks</b> tận dụng mã QR để đơn giản hóa cả hệ thống đặt hàng và phần thưởng khách hàng thân thiết. Bằng cách quét mã tại quầy thanh toán hoặc trên sản phẩm, khách hàng dễ dàng thu thập điểm và đổi phần thưởng.",
          },
          {
            icon: "/icons/coca-cola-96.png",
            content:
              "Trong lĩnh vực thực phẩm và đồ uống, <b>Coca-Cola</b> sử dụng mã QR để thu hút khách hàng tham gia vào các trò chơi tương tác và rút thăm trúng thưởng. Khách hàng quét mã được tìm thấy trên nắp chai để tham gia và giành giải thưởng.",
          },
          {
            icon: "/icons/museum-64.png",
            content:
              "<b>Thư viện</b> và <b>bảo tàng</b> tích hợp mã QR vào các cuộc triển lãm của họ để tạo ra trải nghiệm học tập tương tác. Du khách quét mã để truy cập thông tin toàn diện, video và hướng dẫn về các cuộc triển lãm.",
          },
        ],
      },
      {
        locale: "id",
        title: "Aplikasi Nyata Kode QR di Berbagai Industri",
        data: [
          {
            icon: "/icons/delta-airlines-96.png",
            content:
              "<b>Delta Airlines</b> meningkatkan proses check-in penerbangan dengan mengintegrasikan kode QR pada boarding pass. Penumpang cukup memindai kode tersebut untuk mengakses detail penerbangan secara real-time langsung di smartphone mereka.",
          },
          {
            icon: "/icons/starbucks-96.png",
            content:
              "<b>Starbucks</b> memanfaatkan kode QR untuk menyederhanakan sistem pemesanan dan hadiah loyalitas pelanggan. Dengan memindai kode di kasir atau pada produk, pelanggan dengan mudah mengumpulkan poin dan menukarkan hadiah.",
          },
          {
            icon: "/icons/coca-cola-96.png",
            content:
              "Di sektor makanan dan minuman, <b>Coca-Cola</b> menggunakan kode QR untuk melibatkan pelanggan dalam permainan interaktif dan undian berhadiah. Pelanggan memindai kode yang terdapat pada tutup botol untuk berpartisipasi dan memenangkan hadiah.",
          },
          {
            icon: "/icons/museum-64.png",
            content:
              "<b>Perpustakaan</b> dan <b>museum</b> mengintegrasikan kode QR ke dalam pameran mereka untuk menciptakan pengalaman belajar interaktif. Pengunjung memindai kode untuk mengakses informasi komprehensif, video, dan panduan tentang pameran.",
          },
        ],
      },
      {
        locale: "tr",
        title: "QR Kodlarının Farklı Sektörlerdeki Gerçek Dünya Uygulamaları",
        data: [
          {
            icon: "/icons/delta-airlines-96.png",
            content:
              "<b>Delta Airlines</b>, biniş kartlarına QR kodları entegre ederek uçuş check-in sürecini iyileştiriyor. Yolcular, doğrudan akıllı telefonlarında gerçek zamanlı uçuş detaylarına erişmek için kodu tarıyor.",
          },
          {
            icon: "/icons/starbucks-96.png",
            content:
              "<b>Starbucks</b>, hem sipariş sistemini hem de müşteri sadakat ödüllerini basitleştirmek için QR kodlarını kullanıyor. Müşteriler, kasada veya ürünlerdeki kodları tarayarak zahmetsizce puan toplar ve ödülleri kullanır.",
          },
          {
            icon: "/icons/coca-cola-96.png",
            content:
              "Yiyecek ve içecek sektöründe, <b>Coca-Cola</b>, müşterileri interaktif oyunlara ve çekilişlere dahil etmek için QR kodları kullanıyor. Müşteriler, katılmak ve ödüller kazanmak için şişe kapaklarında bulunan kodları tarıyor.",
          },
          {
            icon: "/icons/museum-64.png",
            content:
              "<b>Kütüphaneler</b> ve <b>müzeler</b>, interaktif öğrenme deneyimleri oluşturmak için sergilerine QR kodları entegre ediyor. Ziyaretçiler, sergiler hakkında kapsamlı bilgilere, videolara ve rehberlere erişmek için kodları tarıyor.",
          },
        ],
      },
      {
        locale: "hi",
        title: "विभिन्न उद्योगों में क्यूआर कोड के वास्तविक दुनिया अनुप्रयोग",
        data: [
          {
            icon: "/icons/delta-airlines-96.png",
            content:
              "<b>डेल्टा एयरलाइंस</b> बोर्डिंग पास पर क्यूआर कोड को एकीकृत करके उड़ान चेक-इन प्रक्रिया को बढ़ाता है। यात्री सीधे अपने स्मार्टफोन पर वास्तविक समय उड़ान विवरण तक पहुंचने के लिए बस कोड को स्कैन करते हैं।",
          },
          {
            icon: "/icons/starbucks-96.png",
            content:
              "<b>स्टारबक्स</b> ऑर्डरिंग सिस्टम और ग्राहक वफादारी पुरस्कार दोनों को सरल बनाने के लिए क्यूआर कोड का लाभ उठाता है। चेकआउट या उत्पादों पर कोड स्कैन करके, ग्राहक आसानी से अंक एकत्र करते हैं और पुरस्कारों का दावा करते हैं।",
          },
          {
            icon: "/icons/coca-cola-96.png",
            content:
              "खाद्य और पेय क्षेत्र में, <b>कोका-कोला</b> इंटरैक्टिव गेम और स्वीपस्टेक में ग्राहकों को शामिल करने के लिए क्यूआर कोड का उपयोग करता है। ग्राहक भाग लेने और पुरस्कार जीतने के लिए बोतल कैप पर पाए जाने वाले कोड को स्कैन करते हैं।",
          },
          {
            icon: "/icons/museum-64.png",
            content:
              "<b>पुस्तकालयों</b> और <b>संग्रहालयों</b> ने इंटरैक्टिव सीखने के अनुभव बनाने के लिए अपने प्रदर्शनों में क्यूआर कोड को एकीकृत किया है। आगंतुक प्रदर्शनों के बारे में व्यापक जानकारी, वीडियो और गाइड तक पहुंचने के लिए कोड को स्कैन करते हैं।",
          },
        ],
      },
      {
        locale: "th",
        title: "การประยุกต์ใช้รหัส QR ในโลกแห่งความเป็นจริงในอุตสาหกรรมต่างๆ",
        data: [
          {
            icon: "/icons/delta-airlines-96.png",
            content:
              "<b>Delta Airlines</b> ปรับปรุงกระบวนการเช็คอินเที่ยวบินโดยการรวมรหัส QR บนบัตรขึ้นเครื่อง ผู้โดยสารเพียงแค่สแกนรหัสเพื่อเข้าถึงรายละเอียดเที่ยวบินแบบเรียลไทม์ได้โดยตรงบนสมาร์ทโฟน",
          },
          {
            icon: "/icons/starbucks-96.png",
            content:
              "<b>Starbucks</b> ใช้ประโยชน์จากรหัส QR เพื่อลดความซับซ้อนทั้งระบบการสั่งซื้อและรางวัลความภักดีของลูกค้า ด้วยการสแกนรหัสที่จุดชำระเงินหรือบนผลิตภัณฑ์ ลูกค้าจะได้รับคะแนนและแลกรางวัลได้อย่างง่ายดาย",
          },
          {
            icon: "/icons/coca-cola-96.png",
            content:
              "ในภาคอาหารและเครื่องดื่ม <b>Coca-Cola</b> ใช้รหัส QR เพื่อให้ลูกค้ามีส่วนร่วมในเกมแบบโต้ตอบและการชิงโชค ลูกค้าสแกนรหัสที่พบในฝาขวดเพื่อเข้าร่วมและรับรางวัล",
          },
          {
            icon: "/icons/museum-64.png",
            content:
              "<b>ห้องสมุด</b>และ<b>พิพิธภัณฑ์</b>ได้รวมรหัส QR เข้ากับนิทรรศการของตนเพื่อสร้างประสบการณ์การเรียนรู้แบบโต้ตอบ ผู้เข้าชมสแกนรหัสเพื่อเข้าถึงข้อมูลที่ครอบคลุม วิดีโอ และคำแนะนำเกี่ยวกับนิทรรศการ",
          },
        ],
      },
    ]);

    return {
      interdata,
      realdata,
    };
  },
});
</script>
